import React                from "react";
import { useNavigate }      from "react-router-dom";
import { useTranslation }   from "react-i18next";
import { Capacitor }        from "@capacitor/core";
import {
    Box, Button, Divider, Paper, Typography, Stack, Avatar,
    // CAMBIO 1: imports necesarios para el selector de idioma
    Select, MenuItem, FormControl, InputAdornment,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
// CAMBIO 2: icono del globo que acompaña al selector
import LanguageIcon from "@mui/icons-material/Language";
import { API_URL } from "../api/index";

export default function Landing({ onLoginClick, onRegisterClick }) {
    const navigate  = useNavigate();
    // CAMBIO 3: extraer también "i18n" del hook para poder leer y cambiar el idioma actual
    const { t, i18n } = useTranslation();
    const haySesion = Boolean(localStorage.getItem("token"));

    // CAMBIO 4: función que cambia el idioma de la app cuando el usuario
    // selecciona ES o EN en el desplegable. El cambio es global: afecta
    // a Landing, Login, Register y cualquier otra pantalla que use t().
    const cambiarIdioma = (e) => {
        i18n.changeLanguage(e.target.value);
    };

    /**
     * Login Google: in-app browser en móvil, redirect directo en web.
     */
    const handleGoogleLogin = async () => {
        const urlGoogle = `${API_URL}/auth/google/login`;
        if (Capacitor.isNativePlatform()) {
            try {
                const { Browser } = await import("@capacitor/browser");
                await Browser.open({
                    url:               `${urlGoogle}?platform=capacitor`,
                    windowName:        "_self",
                    presentationStyle: "popover",
                });
            } catch (err) {
                console.error("Error abriendo browser Google:", err);
            }
        } else {
            window.location.href = urlGoogle;
        }
    };

    /** Testimonios */
    const testimonios = [
        { emoji: "🛒", texto: t("landing.testimonial1"), autor: "Teresa, Las Palmas" },
        { emoji: "💸", texto: t("landing.testimonial2"), autor: "Jorge, Madrid"       },
        { emoji: "📱", texto: t("landing.testimonial3"), autor: "Ana, Sevilla"        },
    ];

    return (
        <Box
            sx={{
                minHeight:      "100vh",
                display:        "flex",
                flexDirection:  "column",
                alignItems:     "center",
                justifyContent: "center",
                bgcolor:        "background.default",
                p:              2,
            }}
        >
            {/* Card principal */}
            <Paper
                elevation={6}
                sx={{
                    width:        "100%",
                    maxWidth:     440,
                    p:            { xs: 3, sm: 4 },
                    borderRadius: 4,
                    textAlign:    "center",
                }}
            >
                {/* CAMBIO 5: selector de idioma en la esquina superior derecha.
                    Posicionado ANTES del logo para que sea lo primero que vea
                    el usuario al entrar. Misma lógica y estilo que en otros
                    sitios donde queramos consistencia visual. */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                            value={i18n.language === "en" ? "en" : "es"}
                            onChange={cambiarIdioma}
                            startAdornment={
                                <InputAdornment position="start">
                                    <LanguageIcon fontSize="small" color="action" />
                                </InputAdornment>
                            }
                            sx={{ borderRadius: 2, fontSize: "0.85rem" }}
                        >
                            <MenuItem value="es">ES</MenuItem>
                            <MenuItem value="en">EN</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Logo */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                    <Avatar
                        src={require("../assets/LogoComPrara.png")}
                        sx={{ width: 96, height: 96, border: "3px solid", borderColor: "primary.light" }}
                    />
                </Box>

                <Typography variant="h4" sx={{ fontWeight: 900, color: "primary.main", mb: 1 }}>
                    {t("landing.title")}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", mb: 0.5 }}>
                    {t("landing.subtitle")}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t("landing.description")}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", display: "block", mb: 3 }}>
                    {t("landing.quote")}
                </Typography>

                {/* Botones principales */}
                <Stack spacing={1.5}>
                    <Button
                        variant="contained" fullWidth size="large"
                        onClick={onLoginClick}
                        sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
                    >
                        {t("landing.login")}
                    </Button>

                    <Button
                        variant="outlined" fullWidth size="large"
                        onClick={onRegisterClick}
                        sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
                    >
                        {t("landing.register")}
                    </Button>

                    {haySesion && (
                        <Button
                            variant="text" fullWidth
                            onClick={() => navigate("/compare")}
                            sx={{ borderRadius: 2, minHeight: 44, textTransform: "none", color: "text.secondary" }}
                        >
                            {t("landing.continue")} →
                        </Button>
                    )}
                </Stack>

                <Divider sx={{ my: 2.5 }}>
                    <Typography variant="caption" color="text.secondary">{t("landing.or")}</Typography>
                </Divider>

                {/* Botón Google */}
                <Button
                    variant="outlined" fullWidth size="large"
                    onClick={handleGoogleLogin}
                    startIcon={
                        <img
                            src={require("../assets/google.png")}
                            alt="Google"
                            style={{ width: 20, height: 20 }}
                        />
                    }
                    sx={{
                        borderRadius:  2,
                        minHeight:     48,
                        fontWeight:    700,
                        textTransform: "none",
                        borderColor:   "divider",
                        color:         "text.primary",
                    }}
                >
                    {t("landing.google")}
                </Button>
            </Paper>

            {/* Testimonios — ocultos en móvil nativo, visibles en web */}
            {!Capacitor.isNativePlatform() && (
                <Box sx={{ width: "100%", maxWidth: 760, mt: 4, px: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, textAlign: "center", mb: 2, color: "text.primary" }}>
                        {t("landing.why")}
                    </Typography>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        {testimonios.map((t, i) => (
                            <Paper
                                key={i}
                                elevation={2}
                                sx={{ flex: 1, p: 2.5, borderRadius: 3, textAlign: "center" }}
                            >
                                <Typography sx={{ fontSize: "2rem", mb: 1 }}>{t.emoji}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t.texto}</Typography>
                                <Typography variant="caption" color="text.disabled">— {t.autor}</Typography>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}